/**
 * @class
 */
import Base from '@netx/core/lib/netx-collection';

import PagerModel from '@netx/core-app/models/pager';
import SorterModel from '@netx/core-app/models/sorter';

import Netx from '@netx/core/bootstrap';
import {dispatcher} from '@netx/core-next/instance/dispatcher';
import {userProfile} from '@netx/core-user/collections/singletons/userProfile';

export default class SharedResources extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/**
	 * @property {object} methods               - Methods for this collection
	 * @property {method} methods.read          - Fetch shared resources for current user
	 * @property {method} methods.fetchForUser  - Fetch shared resources for user
	 */
	get methods() {
		return {
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			read(opts) {
				return [
					'getSharedResources',
					userProfile.id,
					this.resourceName,
					this.modeFilters,
					this.filterStr,
					this.pager.getPageDTO(),
				];
			},

			/**
			 * Admins only
			 * @method
			 * @param {number} userId User id
			 * @param {object} opts Request options
			 */
			fetchForUser(userId, opts) {
				return [
					'getSharedResources',
					userId,
					this.resourceName,
					this.modeFilters,
					this.filterStr,
					this.pager.getPageDTO(),
				];
			},
		};
	}

	/**
	 * @property {object} parsers               - Parsers for this collection
	 * @property {method} parsers.fetchForUser  - Parse response of user shared resources
	 */
	get parsers() {
		return {
			/**
			 * @function
			 * @param {array} data Shared resources
			 * @param {number} userId User id
			 * @param {object} opts Request options
			 */
			fetchForUser(data, userId, opts) {
				opts = opts || {};

				this.set(data, opts);
				!opts.silent && this.trigger('sync', this, data, opts);
			},
		};
	}

	/** @override */
	constructor(models, opts = {}) {
		super(models, opts);

		this.resourceName = opts.resourceName;

		/**
		 * This is non negotiable - we use the fields supported by `sharedResourceSortDTO`
		 * @memberof module:share/collections/sharedResources
		 * @override
		 * @alias sortableFields
		 * @type {array}
		 */
		this.sortableFields = opts.sortableFields || [
			'id',
			'creationDate',
			'contentCount',
			'modDate',
			'title',
		];

		/**
		 * Mode filters (read/write/delete/favorites)
		 * @memberof module:share/collections/sharedResources
		 * @alias modeFilters
		 * @type {array}
		 */
		this.modeFilters = opts.modeFilters || null;

		/**
		 * Paging model
		 * @memberof module:share/collections/sharedResources
		 * @alias pager
		 * @type {module:app/models/pager}
		 */
		this.pager = new PagerModel(_.extend({}, opts.pager), {collection: this});

		/**
		 * Sorting model
		 * @memberof module:asset/models/assetProject
		 * @alias sorter
		 * @type {module:app/models/sorter}
		 */
		this.sorter = new SorterModel(
			{
				field: opts.sortField || 'title',
				order: opts.sortOrder || 'desc',
			},
			{
				collection: this,
				sortableFields: this.sortableFields,
			},
		);

		/**
		 * Update main collections when a shared resource is toggled as favorite
		 * @listens share:sharedResource:toggle:favorite:success
		 * @param {module:share/models/sharedResource} sharedResource Shared resource
		 * @param {boolean} isFavorite Is favorite
		 */
		this.listenTo(
			dispatcher,
			'share:sharedResource:toggle:favorite:success',
			(sharedResource, isFavorite) => {
				if (sharedResource.resourceName !== this.resourceName) return;

				const model = this.get(sharedResource.id);

				// Existing, reflect `isFavorite`
				model && model.set('isFavorite', isFavorite);

				// We are filtered down to favorites, add or remove the model
				if (this.hasModeFilter('favorites')) {
					isFavorite
						? this.add(sharedResource.attributes, {merge: true})
						: model && this.remove(model);
				}
			},
		);
	}

	/**
	 * @method
	 * @param {string} mode Mode
	 * @return {boolean} Has mode filter
	 */
	hasModeFilter(mode) {
		return this.modeFilters && _.contains(this.modeFilters, mode);
	}

	/** @override */
	isFiltered() {
		const filtered = super.isFiltered();
		return filtered || this.filterStr !== '' || this.selectedMode != null;
	}

	/**
	 * @method
	 * @param {module:share/models/sharedResource} model Shared resource
	 * @return {boolean}
	 */
	matchesFilters(model) {
		if (
			this.filterStr !== '' &&
			!model.getTitle().toLowerCase().match(this.filterStr.toLowerCase())
		)
			return false;

		if (this.hasModeFilter('favorites')) {
			return model.isFavorite();
		}

		return this.selectedMode ? model.allows(this.selectedMode) : true;
	}

	/**
	 * Reset the mode filter and refetch
	 * @method
	 */
	resetModeFilter() {
		// Nothing to reset
		if (this.selectedMode == null) return new $.Deferred().resolve();

		this.selectedMode = this.modeFilters = null;
		// Reset to first page
		this.pager.firstPage();
		// Fetch the unfiltered data
		return this.fetch({duplicateQueryPolicy: 'abort'});
	}

	/**
	 * Set mode filter refetch
	 * @method
	 * @param {string} mode Mode filter
	 */
	setModeFilter(mode) {
		// No filter, reset
		if (!mode) return this.resetModeFilter();

		// Nothing to do
		if (this.selectedMode === mode) return new $.Deferred().resolve();

		this.selectedMode = mode;

		switch (mode) {
			// In cases of write (edit)- a user can write to anything they can delete
			case 'write':
				this.modeFilters = ['delete', 'write'];
				break;
			// Favorites
			// case 'favorites':
			// The following will enforce that permission ONLY for filtering
			// Manage
			// case 'delete':
			// Read-only
			// case 'read':
			default:
				this.modeFilters = [mode];
				break;
		}

		// Reset to first page
		this.pager.firstPage();

		return this.fetch({duplicateQueryPolicy: 'abort'});
	}

	/** @type {string} */
	static get __name__() {
		return 'SharedResources';
	}
}
